import {
  Centralizer,
  DetailsInfo,
  Loading,
  NotFound,
  Text,
} from '@/components';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { FALLBACK_TEXT, NEST_ITEM_CONNECTOR, datetimeFormat } from '@/utils';
import type { AxiosError } from 'axios';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecursiveOrganizationFilteredByKey } from '../../organization/_hooks/organization';
import { UserName } from '../_components/UserName';
import { useUser } from '../_hooks/user';

export const UserDetailsPage: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id } = useId();
  const user = useUser(id);

  const { data: connectedOrganizationNames } =
    useRecursiveOrganizationFilteredByKey({
      params: { ids: user.data?.organizationIds ?? [] },
      options: {
        reverse: true,
        filterBy: 'name',
        separator: NEST_ITEM_CONNECTOR,
      },
      select: (data) => data.join(', '),
    });

  if (user.isPending || !user.data) {
    return <Loading />;
  }

  if (user.isError) {
    if ((user.error as AxiosError).response?.status === 404) {
      return <NotFound backRoute="/tenant-manager" />;
    }
    openMessage('error', t('failed-fetch-user'));
    return null;
  }

  return (
    <Centralizer>
      <DetailsInfo
        items={[
          {
            label: t('name'),
            value: (
              <UserName
                name={user.data.name}
                isIntegrated={user.data.isIntegrated}
              />
            ),
          },
          {
            label: t('email'),
            value: (
              <Text weight="regular" text={user.data.email ?? FALLBACK_TEXT} />
            ),
          },
          {
            label: t('email-notification-for-comment-and-workflow'),
            value: (
              <Text
                weight="regular"
                text={
                  user.data.isEmailNotificationEnabled
                    ? t('email-notification-target')
                    : t('email-notification-non-target')
                }
              />
            ),
          },
          {
            label: t('organization-name'),
            value: (
              <Text
                weight="regular"
                text={connectedOrganizationNames || FALLBACK_TEXT}
              />
            ),
          },
          {
            label: t('created-datetime'),
            value: (
              <Text
                weight="regular"
                text={datetimeFormat({ datetime: user.data.createdAt })}
              />
            ),
          },
          {
            label: t('updated-datetime'),
            value: (
              <Text
                weight="regular"
                text={
                  user.data.modifiedAt
                    ? datetimeFormat({ datetime: user.data.modifiedAt })
                    : null
                }
              />
            ),
          },
        ]}
      />
    </Centralizer>
  );
};
